import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import {
    DefaultButton,
    PrimaryButton,
} from '@bb-ui/react-library/dist/components/Button';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { RadioGroup } from '@bb-ui/react-library/dist/components/RadioGroup';
import { Radio } from '@bb-ui/react-library/dist/components/Radio';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import { CircularProgress } from '@bb-ui/react-library/dist/components/Progress';
import { CheckCircle } from '@bb-ui/icons/dist/small/CheckCircle';
import { useSharedStyles } from '../../../../utilities/sharedStyles';
import { useDialogTabWrap } from '../../../../hooks/useDialogTabWrap';
import {
    AddAttempt,
    AddFailureReason,
    AddIdentityProviderInput,
    GUIDED_TYPE_SUFFIX,
    IdpGuidanceType,
    IN_FLIGHT_PROVISIONING_STATES,
    ProvisioningState,
} from '../identityProviderManagement.types';
import { nameKey } from '../identityProviderRows';
import { SetupGuide } from '../SetupGuide/SetupGuide';
import { useStyles } from '../Authentication.styles';
import { TransText } from '../transHelper';

const TYPES: IdpGuidanceType[] = ['ENTRA', 'OKTA', 'SHIBBOLETH', 'OTHER'];

const TYPE_SUFFIX = GUIDED_TYPE_SUFFIX;

const STEP_KEYS = ['stepChooseType', 'stepConfigure', 'stepReview'] as const;
const TOTAL_STEPS = STEP_KEYS.length;

type Step = 1 | 2 | 3;

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * The add attempt to show progress and outcome for. When it is given as
     * the wizard opens, the wizard starts on that add attempt instead of an
     * empty form. The wizard never changes an add attempt; the owner keeps it.
     */
    attempt?: AddAttempt;
    /** Start a new add attempt. The owner then passes it back as `attempt`. */
    onSubmit: (input: AddIdentityProviderInput) => void;
    /**
     * Stop showing `attempt`, so a corrected form is reviewed without the
     * last outcome. The add attempt itself is not affected.
     */
    onRelease: () => void;
    /** Prefills the form, e.g. to retry a failed add attempt. */
    initialValues?: AddIdentityProviderInput;
    /**
     * The wizard was opened on `attempt` because it is still running and
     * only one add attempt runs at a time. The wizard says so.
     */
    openedOnAddInFlight?: boolean;
    /** Names the provider may not use, in `nameKey` form. */
    takenNames?: ReadonlySet<string>;
    /** Opens the test dialog (owned by the list) for the provider just added. */
    onTest?: (target: { displayName: string; connectionName: string }) => void;
}

interface Fields {
    displayName: string;
    metadataUrl: string;
    nameIdentifier: string;
}

const NO_NAMES: ReadonlySet<string> = new Set();

const HTTPS_PREFIX = /^https:\/\//;

const isHttpsUrl = (value: string) => {
    try {
        return HTTPS_PREFIX.test(value) && new URL(value).hostname !== '';
    } catch {
        return false;
    }
};

const REJECTED_COPY: Record<AddFailureReason, string> = {
    METADATA_URL_UNREADABLE: 'submitFailedMetadataUrl',
};

/** Outcomes the admin can fix and resubmit. A timeout is not one: it may yet succeed. */
const RETRYABLE: ReadonlyArray<ProvisioningState> = [
    ProvisioningState.SUBMIT_REJECTED,
    ProvisioningState.FAILED,
];

const STAGES: ReadonlyArray<{
    key: string;
    done: ReadonlyArray<ProvisioningState>;
    current: ReadonlyArray<ProvisioningState>;
}> = [
    {
        key: 'stageSubmitted',
        done: [
            ProvisioningState.SUBMITTED,
            ProvisioningState.PROVISIONING,
            ProvisioningState.PROVISIONED,
        ],
        current: [ProvisioningState.SUBMITTING],
    },
    {
        key: 'stageProvisioning',
        done: [ProvisioningState.PROVISIONED],
        current: [ProvisioningState.SUBMITTED, ProvisioningState.PROVISIONING],
    },
    { key: 'stageReady', done: [ProvisioningState.PROVISIONED], current: [] },
];

/**
 * Three-step add flow in a modal dialog: choose type, configure, review & provision.
 */
export const AddIdentityProviderWizard: React.FunctionComponent<Props> = ({
    open,
    onClose,
    attempt,
    onSubmit,
    onRelease,
    onTest,
    initialValues,
    openedOnAddInFlight = false,
    takenNames = NO_NAMES,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const sharedClasses = useSharedStyles();
    useDialogTabWrap(open);

    const [step, setStep] = React.useState<Step>(1);
    const [type, setType] = React.useState<IdpGuidanceType>('OTHER');
    const [fields, setFields] = React.useState<Fields>({
        displayName: '',
        metadataUrl: '',
        nameIdentifier: '',
    });
    const [showErrors, setShowErrors] = React.useState(false);

    const state = attempt?.state ?? ProvisioningState.IDLE;
    /** The last step shows an add attempt's progress rather than the review. */
    const confirmed = !!attempt && step === 3;
    const connectionName = attempt?.connectionName;
    const rejectedReason = attempt?.rejectedReason;

    const typeName = t(
        `settings.authentication.wizard.type${TYPE_SUFFIX[type]}`,
    );

    const wasOpen = React.useRef(false);
    React.useEffect(() => {
        if (open && !wasOpen.current) {
            // Opened on an add attempt in flight: show its progress on the last
            // step. Otherwise start on an empty or prefilled form.
            const seed = attempt?.input ?? initialValues;
            setStep(attempt ? 3 : 1);
            setType(seed?.idpType ?? 'OTHER');
            setFields({
                displayName: seed?.displayName ?? '',
                metadataUrl: seed?.metadataUrl ?? '',
                nameIdentifier: seed?.nameIdentifier ?? '',
            });
            setShowErrors(false);
        }
        wasOpen.current = open;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const focusStepHeading = React.useCallback(
        (node: HTMLHeadingElement | null) => node?.focus(),
        [],
    );

    /*
     * The requirement is the same whatever the type -- the URL has to be
     * published and reachable -- so it is said once, and where to find it is
     * appended for the consoles that have a known path to it. Other has no such
     * path, so it gets the requirement alone.
     */
    const metadataHint = (
        <>
            {/* prettier-ignore */}
            <Trans i18nKey="settings.authentication.wizard.metadataHint">
                Your identity provider's published <span lang="en">SAML</span> 2.0 metadata URL (must be publicly accessible).
            </Trans>
            {type !== 'OTHER' && (
                <>
                    {' '}
                    <TransText
                        text={t(
                            `settings.authentication.wizard.metadataHint${TYPE_SUFFIX[type]}`,
                        )}
                        tags={[<span key="english" lang="en" />]}
                    />
                </>
            )}
        </>
    );

    const errors = React.useMemo(() => {
        const required = (value: string) =>
            value.trim()
                ? undefined
                : t('settings.authentication.wizard.required');

        const metadataUrlError = () => {
            const trimmed = fields.metadataUrl.trim();
            if (!trimmed) return t('settings.authentication.wizard.required');
            return isHttpsUrl(trimmed)
                ? undefined
                : t('settings.authentication.wizard.invalidUrl');
        };

        const displayNameError = () =>
            required(fields.displayName) ??
            (takenNames.has(nameKey(fields.displayName))
                ? t('settings.authentication.wizard.nameTaken')
                : undefined);

        return {
            displayName: displayNameError(),
            metadataUrl: metadataUrlError(),
            nameIdentifier: required(fields.nameIdentifier),
        };
    }, [fields, t, takenNames]);

    const firstInvalid = (
        ['displayName', 'metadataUrl', 'nameIdentifier'] as const
    ).find(k => errors[k]);

    const fieldRefs = {
        displayName: React.useRef<HTMLInputElement>(null),
        metadataUrl: React.useRef<HTMLInputElement>(null),
        nameIdentifier: React.useRef<HTMLInputElement>(null),
    };

    /** Step 2 -> 3 is the validation gate; nothing is sent until step 3 is confirmed. */
    const goToReview = () => {
        setShowErrors(true);
        if (firstInvalid) {
            // Send focus to the first thing that needs fixing, not to the top.
            fieldRefs[firstInvalid].current?.focus();
            return;
        }
        // The last outcome does not apply to the corrected values.
        onRelease();
        setStep(3);
    };

    /** Back to the form after a failed attempt, keeping the reason on screen. */
    const backToForm = () => {
        setStep(2);
    };

    const handleSubmit = () => {
        onSubmit({ ...fields, idpType: type });
    };

    const set =
        (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
            setFields(f => ({ ...f, [key]: e.target.value }));

    const headingId = 'add-idp-wizard-title';
    const provisioningFailed =
        state === ProvisioningState.FAILED ||
        state === ProvisioningState.TIMED_OUT;
    const canRetry = RETRYABLE.includes(state);

    const rejectedCopy = t(
        `settings.authentication.wizard.${
            rejectedReason ? REJECTED_COPY[rejectedReason] : 'submitFailed'
        }`,
    );
    const failureCopies: Partial<Record<ProvisioningState, string>> = {
        [ProvisioningState.SUBMIT_REJECTED]: rejectedCopy,
        [ProvisioningState.FAILED]: t(
            'settings.authentication.wizard.provisioningFailed',
        ),
    };
    const failureCopy = failureCopies[state];

    const stageMarker = (done: boolean, isCurrent: boolean) => {
        if (done) {
            return (
                <CheckCircle className={classes.stageIcon} aria-hidden="true" />
            );
        }
        if (isCurrent) {
            return (
                <CircularProgress
                    size={14}
                    ariaLabel={t(
                        'settings.authentication.list.provisioningBusy',
                    )}
                />
            );
        }
        return <span className={classes.stageDot} aria-hidden="true" />;
    };

    const stepIndicator = (
        <ol
            className={classes.stepList}
            aria-label={t('settings.authentication.wizard.progressLabel')}
            data-testid="wizardStepIndicator"
        >
            {STEP_KEYS.map((key, index) => {
                const number = index + 1;
                const isCurrent = number === step;
                const isDone = number < step;
                return (
                    <li
                        key={key}
                        className={classnames(classes.stepItem, {
                            [classes.stepItemCurrent]: isCurrent,
                        })}
                        aria-current={isCurrent ? 'step' : undefined}
                    >
                        <span
                            className={classnames(classes.stepNumber, {
                                [classes.stepNumberCurrent]: isCurrent,
                                [classes.stepNumberDone]: isDone,
                            })}
                            aria-hidden="true"
                        >
                            {isDone ? '✓' : number}
                        </span>
                        {t(`settings.authentication.wizard.${key}`)}
                        {isDone && (
                            <span className={sharedClasses.srOnly}>
                                {` ${t(
                                    'settings.authentication.wizard.stepDone',
                                )}`}
                            </span>
                        )}
                    </li>
                );
            })}
        </ol>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby={headingId}
            maxWidth={false}
            classes={{ paper: classes.wizardPaper }}
            data-testid="addIdentityProviderWizard"
        >
            <DialogTitle
                id={headingId}
                onClose={onClose}
                closeLabel={t('general.close')}
            >
                {t('settings.authentication.wizard.title')}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {stepIndicator}
                <Typography
                    variant="body2"
                    className={sharedClasses.srOnly}
                    data-testid="wizardStepLabel"
                >
                    {t('settings.authentication.wizard.stepOf', {
                        current: step,
                        total: TOTAL_STEPS,
                    })}
                </Typography>

                {step === 1 && (
                    <>
                        <h3
                            ref={focusStepHeading}
                            tabIndex={-1}
                            className={classes.stepHeading}
                        >
                            {t('settings.authentication.wizard.step1Heading')}
                        </h3>
                        <Typography
                            variant="body2"
                            className={classes.sectionIntro}
                        >
                            {/* prettier-ignore */}
                            <Trans i18nKey="settings.authentication.wizard.step1Sub">
                                Pick the closest match — we'll tailor the setup instructions. All types configure through the same secure <span lang="en">SAML</span> connection.
                            </Trans>
                        </Typography>
                        <RadioGroup
                            legendText={t(
                                'settings.authentication.wizard.typeLegend',
                            )}
                            value={type}
                            onChange={(_, v) => setType(v as IdpGuidanceType)}
                            className={classes.typeGrid}
                        >
                            {TYPES.map(option => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio />}
                                    className={classnames(classes.typeCard, {
                                        [classes.typeCardSelected]:
                                            option === type,
                                    })}
                                    data-testid={`idpType-${option}`}
                                    label={
                                        <>
                                            <span
                                                className={classes.typeCardName}
                                            >
                                                <TransText
                                                    text={t(
                                                        `settings.authentication.wizard.type${TYPE_SUFFIX[option]}`,
                                                    )}
                                                    tags={[
                                                        <span
                                                            key="english"
                                                            lang="en"
                                                        />,
                                                    ]}
                                                />
                                            </span>
                                            <span
                                                className={classes.typeCardDesc}
                                            >
                                                <TransText
                                                    text={t(
                                                        `settings.authentication.wizard.typeDesc${TYPE_SUFFIX[option]}`,
                                                    )}
                                                    tags={[
                                                        <span
                                                            key="english"
                                                            lang="en"
                                                        />,
                                                    ]}
                                                />
                                            </span>
                                        </>
                                    }
                                />
                            ))}
                        </RadioGroup>
                        <Banner
                            severity="info"
                            className={classes.guidance}
                            data-testid="wizardGuidance"
                        >
                            <strong>
                                <TransText
                                    text={t(
                                        'settings.authentication.wizard.aboutType',
                                        { name: typeName },
                                    )}
                                    tags={[<span key="english" lang="en" />]}
                                />
                            </strong>{' '}
                            <TransText
                                text={t(
                                    `settings.authentication.wizard.guidance${TYPE_SUFFIX[type]}`,
                                )}
                                tags={[<span key="english" lang="en" />]}
                            />
                        </Banner>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h3
                            ref={focusStepHeading}
                            tabIndex={-1}
                            className={classes.stepHeading}
                        >
                            <TransText
                                text={t(
                                    'settings.authentication.wizard.step2Heading',
                                    { name: typeName },
                                )}
                                tags={[<span key="english" lang="en" />]}
                            />
                        </h3>
                        <Typography
                            variant="body2"
                            className={classes.sectionIntro}
                        >
                            {t('settings.authentication.wizard.step2Sub', {
                                name: typeName,
                            })}
                        </Typography>
                        {failureCopy && (
                            <Banner
                                severity="error"
                                className={classes.banner}
                                data-testid="wizardSubmitError"
                            >
                                <TransText
                                    text={failureCopy}
                                    tags={[<span key="english" lang="en" />]}
                                />
                            </Banner>
                        )}
                        <div className={classes.fieldColumn}>
                            <TextField
                                id="idp-displayName"
                                data-testid="idp-displayName"
                                inputRef={fieldRefs.displayName}
                                label={t(
                                    'settings.authentication.wizard.displayName',
                                )}
                                value={fields.displayName}
                                onChange={set('displayName')}
                                error={showErrors && !!errors.displayName}
                                helperText={
                                    showErrors && errors.displayName
                                        ? errors.displayName
                                        : t(
                                              'settings.authentication.wizard.displayNameHint',
                                          )
                                }
                                required
                                fullWidth
                            />
                            <TextField
                                id="idp-metadataUrl"
                                data-testid="idp-metadataUrl"
                                inputRef={fieldRefs.metadataUrl}
                                label={
                                    <Trans i18nKey="settings.authentication.wizard.metadataUrl">
                                        <span key="english" lang="en">
                                            SAML
                                        </span>{' '}
                                        metadata URL
                                    </Trans>
                                }
                                value={fields.metadataUrl}
                                onChange={set('metadataUrl')}
                                error={showErrors && !!errors.metadataUrl}
                                helperText={
                                    showErrors && errors.metadataUrl
                                        ? errors.metadataUrl
                                        : metadataHint
                                }
                                required
                                fullWidth
                            />
                            <TextField
                                id="idp-nameIdentifier"
                                data-testid="idp-nameIdentifier"
                                inputRef={fieldRefs.nameIdentifier}
                                label={t(
                                    'settings.authentication.wizard.nameIdentifier',
                                )}
                                value={fields.nameIdentifier}
                                onChange={set('nameIdentifier')}
                                error={showErrors && !!errors.nameIdentifier}
                                helperText={
                                    showErrors && errors.nameIdentifier ? (
                                        errors.nameIdentifier
                                    ) : (
                                        // prettier-ignore
                                        <Trans i18nKey="settings.authentication.wizard.nameIdentifierHint">
                                            The <span lang="en">SAML</span> attribute <span lang="en">Illuminate</span> should use to match this user's account.
                                        </Trans>
                                    )
                                }
                                required
                                fullWidth
                            />
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h3
                            ref={focusStepHeading}
                            tabIndex={-1}
                            className={classes.stepHeading}
                        >
                            {t('settings.authentication.wizard.step3Heading')}
                        </h3>
                        <Typography
                            variant="body2"
                            className={classes.sectionIntro}
                        >
                            {t('settings.authentication.wizard.step3Sub')}
                        </Typography>

                        {openedOnAddInFlight &&
                            IN_FLIGHT_PROVISIONING_STATES.has(state) && (
                                <Banner
                                    severity="info"
                                    className={classes.banner}
                                    data-testid="wizardAddInProgress"
                                >
                                    {t(
                                        'settings.authentication.wizard.addInProgress',
                                    )}
                                </Banner>
                            )}

                        {state === ProvisioningState.SUBMIT_REJECTED && (
                            <Banner
                                severity="error"
                                className={classes.banner}
                                data-testid="wizardSubmitError"
                            >
                                {rejectedCopy}
                            </Banner>
                        )}

                        <dl
                            className={classes.definitionList}
                            data-testid="wizardReview"
                        >
                            {(
                                [
                                    [
                                        'reviewType',
                                        <TransText
                                            key="reviewType"
                                            text={t(
                                                'settings.authentication.wizard.reviewTypeValue',
                                                { name: typeName },
                                            )}
                                            tags={[
                                                <span
                                                    key="english"
                                                    lang="en"
                                                />,
                                            ]}
                                        />,
                                    ],
                                    ['reviewDisplayName', fields.displayName],
                                    ['reviewMetadataUrl', fields.metadataUrl],
                                    [
                                        'reviewNameIdentifier',
                                        fields.nameIdentifier,
                                    ],
                                ] as const
                            ).map(([key, value]) => (
                                <div
                                    className={classes.definitionRow}
                                    key={key}
                                >
                                    <dt className={classes.definitionTerm}>
                                        {t(
                                            `settings.authentication.wizard.${key}`,
                                        )}
                                    </dt>
                                    <dd className={classes.definitionValue}>
                                        {value}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <Typography variant="subtitle2" component="h4">
                            {t(
                                'settings.authentication.wizard.provisioningHeading',
                            )}
                        </Typography>
                        <ol
                            className={classes.stageList}
                            data-testid="provisioningStages"
                        >
                            {STAGES.map(stage => {
                                const done = stage.done.includes(state);
                                const isCurrent =
                                    confirmed &&
                                    !done &&
                                    !provisioningFailed &&
                                    stage.current.includes(state);
                                return (
                                    <li
                                        key={stage.key}
                                        className={classnames(
                                            classes.stageItem,
                                            {
                                                [classes.stageItemCurrent]:
                                                    isCurrent,
                                            },
                                        )}
                                        aria-current={
                                            isCurrent ? 'step' : undefined
                                        }
                                        data-testid={`stage-${stage.key}`}
                                    >
                                        {stageMarker(done, isCurrent)}
                                        {t(
                                            `settings.authentication.wizard.${stage.key}`,
                                        )}
                                    </li>
                                );
                            })}
                        </ol>

                        {/* Not a live region: each outcome banner announces itself. */}
                        <div data-testid="provisioningOutcome">
                            {!confirmed &&
                                t(
                                    'settings.authentication.wizard.provisioningNotStarted',
                                )}
                            {confirmed &&
                                state === ProvisioningState.PROVISIONED && (
                                    <Banner
                                        severity="success"
                                        className={classes.banner}
                                        data-testid="provisioningSucceeded"
                                    >
                                        <Trans
                                            i18nKey="settings.authentication.wizard.provisioningActive"
                                            values={{
                                                name: fields.displayName,
                                            }}
                                        >
                                            <strong>
                                                {{ name: fields.displayName }}
                                            </strong>{' '}
                                            is ready. Finish setting it up in
                                            your identity provider using the
                                            guide below.
                                        </Trans>
                                    </Banner>
                                )}
                            {state === ProvisioningState.FAILED && (
                                <Banner
                                    severity="error"
                                    className={classes.banner}
                                    data-testid="provisioningFailed"
                                >
                                    {t(
                                        'settings.authentication.wizard.provisioningFailed',
                                    )}
                                </Banner>
                            )}
                            {state === ProvisioningState.TIMED_OUT && (
                                <Banner
                                    severity="warning"
                                    className={classes.banner}
                                    data-testid="provisioningTimedOut"
                                >
                                    {t(
                                        'settings.authentication.wizard.provisioningTimedOut',
                                    )}
                                </Banner>
                            )}
                        </div>

                        {confirmed &&
                            !provisioningFailed &&
                            state !== ProvisioningState.SUBMIT_REJECTED &&
                            state !== ProvisioningState.PROVISIONED && (
                                <Banner
                                    severity="info"
                                    className={classes.banner}
                                    data-testid="provisioningCallout"
                                >
                                    {t(
                                        'settings.authentication.wizard.provisioningCallout',
                                        { name: fields.displayName },
                                    )}
                                </Banner>
                            )}

                        {state === ProvisioningState.PROVISIONED && (
                            <>
                                <Typography
                                    variant="subtitle2"
                                    component="h4"
                                    data-testid="setupHeading"
                                >
                                    {t(
                                        'settings.authentication.wizard.setupHeading',
                                        { name: typeName },
                                    )}
                                </Typography>
                                <SetupGuide
                                    type={type}
                                    connectionName={connectionName}
                                />
                                {onTest && connectionName && (
                                    <Typography
                                        variant="body2"
                                        data-testid="wizard-test-hint"
                                    >
                                        {/* prettier-ignore */}
                                        <Trans i18nKey="settings.authentication.wizard.testHint">
                                            When you’ve finished setting up your identity provider, test the sign-in. The test checks that sign-in works and shows which username to use for its users in <span lang="en">Illuminate</span>.
                                        </Trans>
                                    </Typography>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {confirmed ? (
                    <>
                        <DefaultButton
                            onClick={onClose}
                            data-testid="wizardClose"
                        >
                            {t('settings.authentication.wizard.close')}
                        </DefaultButton>
                        {state === ProvisioningState.PROVISIONED &&
                            onTest &&
                            connectionName && (
                                <PrimaryButton
                                    onClick={() =>
                                        onTest({
                                            displayName: fields.displayName,
                                            connectionName,
                                        })
                                    }
                                    data-testid="wizard-test"
                                >
                                    {t('settings.authentication.wizard.test')}
                                </PrimaryButton>
                            )}
                        {canRetry && (
                            <>
                                <DefaultButton
                                    onClick={backToForm}
                                    data-testid="wizardBack"
                                >
                                    {t('settings.authentication.wizard.back')}
                                </DefaultButton>
                                <PrimaryButton
                                    onClick={handleSubmit}
                                    data-testid="wizardRetry"
                                >
                                    {t('settings.authentication.wizard.retry')}
                                </PrimaryButton>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <DefaultButton
                            onClick={onClose}
                            data-testid="wizardCancel"
                        >
                            {t('settings.authentication.wizard.cancel')}
                        </DefaultButton>
                        {step > 1 && (
                            <DefaultButton
                                onClick={() => setStep((step - 1) as Step)}
                                data-testid="wizardBack"
                            >
                                {t('settings.authentication.wizard.back')}
                            </DefaultButton>
                        )}
                        {step === 1 && (
                            <PrimaryButton
                                onClick={() => setStep(2)}
                                data-testid="wizardNext"
                            >
                                {t('settings.authentication.wizard.next')}
                            </PrimaryButton>
                        )}
                        {step === 2 && (
                            <PrimaryButton
                                onClick={goToReview}
                                data-testid="wizardNext"
                            >
                                {t('settings.authentication.wizard.next')}
                            </PrimaryButton>
                        )}
                        {step === 3 && (
                            <PrimaryButton
                                onClick={handleSubmit}
                                disabled={
                                    state === ProvisioningState.SUBMITTING
                                }
                                data-testid="wizardSubmit"
                            >
                                {t('settings.authentication.wizard.submit')}
                            </PrimaryButton>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
